<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  minLength,
  email as emailValidator,
} from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  currentChat: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['cancel']);

const store = useStore();
const { t } = useI18n();

const dialogRef = ref(null);
const email = ref('');
const selectedType = ref('');
const isSubmitting = ref(false);

const rules = {
  email: {
    required,
    email: emailValidator,
    minLength: minLength(4),
  },
};

const v$ = useVuelidate(rules, { email });

const sentToOtherEmailAddress = computed(
  () => selectedType.value === 'other_email_address'
);

const isFormValid = computed(() => {
  if (selectedType.value) {
    if (sentToOtherEmailAddress.value) {
      return !!email.value && !v$.value.email.$error;
    }
    return true;
  }
  return false;
});

const selectedEmailAddress = computed(() => {
  const { meta } = props.currentChat;
  switch (selectedType.value) {
    case 'contact':
      return meta.sender.email;
    case 'assignee':
      return meta.assignee.email;
    case 'other_email_address':
      return email.value;
    default:
      return '';
  }
});

onMounted(() => {
  dialogRef.value?.open();
});

const onCancel = () => {
  emit('cancel');
};

const onSubmit = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch('sendEmailTranscript', {
      email: selectedEmailAddress.value,
      conversationId: props.currentChat.id,
    });
    useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_SUCCESS'));
    onCancel();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 402) {
      useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_PAYMENT_REQUIRED'));
    } else {
      useAlert(t('EMAIL_TRANSCRIPT.SEND_EMAIL_ERROR'));
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    :title="$t('EMAIL_TRANSCRIPT.TITLE')"
    :description="$t('EMAIL_TRANSCRIPT.DESC')"
    :confirm-button-label="$t('EMAIL_TRANSCRIPT.SUBMIT')"
    :cancel-button-label="$t('EMAIL_TRANSCRIPT.CANCEL')"
    :disable-confirm-button="!isFormValid"
    :is-loading="isSubmitting"
    @confirm="onSubmit"
    @close="onCancel"
  >
    <div class="w-full">
      <div
        v-if="currentChat.meta.sender && currentChat.meta.sender.email"
        class="flex items-center gap-2"
      >
        <input
          id="contact"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="contact"
        />
        <label for="contact">{{
          $t('EMAIL_TRANSCRIPT.FORM.SEND_TO_CONTACT')
        }}</label>
      </div>
      <div
        v-if="
          currentChat.meta.assignee &&
          currentChat.meta.assignee_type !== 'AgentBot'
        "
        class="flex items-center gap-2"
      >
        <input
          id="assignee"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="assignee"
        />
        <label for="assignee">{{
          $t('EMAIL_TRANSCRIPT.FORM.SEND_TO_AGENT')
        }}</label>
      </div>
      <div class="flex items-center gap-2">
        <input
          id="other_email_address"
          v-model="selectedType"
          type="radio"
          name="selectedType"
          value="other_email_address"
        />
        <label for="other_email_address">{{
          $t('EMAIL_TRANSCRIPT.FORM.SEND_TO_OTHER_EMAIL_ADDRESS')
        }}</label>
      </div>
      <div v-if="sentToOtherEmailAddress" class="w-[50%] mt-1">
        <label :class="{ error: v$.email.$error }">
          <input
            v-model="email"
            type="text"
            :placeholder="$t('EMAIL_TRANSCRIPT.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.email.$touch"
          />
          <span v-if="v$.email.$error" class="message">
            {{ $t('EMAIL_TRANSCRIPT.FORM.EMAIL.ERROR') }}
          </span>
        </label>
      </div>
    </div>
  </Dialog>
</template>
